/**
 * Central version constants + resolver used by both the global AppFooter and
 * the Login screen. Keep this in one place so bumping a release only requires
 * editing the two constants below.
 *
 * ``resolveDisplayVersion`` reconciles the version baked into the shipped JS
 * bundle (``FALLBACK_VERSION``) with whatever an admin most recently
 * published to the DB via ``POST /api/system/version``. We take the numerical
 * ``max()`` so a DB that is behind the deployed code can never visibly
 * downgrade the footer (the bug that produced the "flash new → revert to
 * old version" report on refresh in production).
 */

export const FALLBACK_VERSION = '26.10.01';
export const FALLBACK_BUILD_DATE = '2026-10-18';

/**
 * Numeric-part comparator for our ``YY.MM.PP`` version strings.
 * Returns 1 if ``a`` is newer, -1 if older, 0 if equal or uncomparable.
 */
export const compareVersions = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const parse = (v) => String(v).split('.').map((p) => {
    const n = parseInt(p, 10);
    return Number.isNaN(n) ? 0 : n;
  });
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
};

/**
 * Given a DB-published version + optional publishedAt, return the version and
 * build-date pair that should actually be rendered. Falls back to the shipped
 * constants when the DB value is missing or older than the shipped code.
 */
export const resolveDisplayVersion = (dbVersion, dbPublishedAt) => {
  const useDb = dbVersion && compareVersions(dbVersion, FALLBACK_VERSION) >= 0;
  const version = useDb ? dbVersion : FALLBACK_VERSION;
  const build = (useDb && formatBuildDate(dbPublishedAt)) || FALLBACK_BUILD_DATE;
  return { version, build };
};

export const formatBuildDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch (e) {
    return null;
  }
};
